import { useRoute } from '@shared/composables/useRoute';
import { Payout, type IPayout, type PayoutStatus } from '@app/purchase/payout/models/Payout';
import type { FormOptions } from '@shared/types/Form';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useForm } from '@shared/composables/useForm';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { usePayload } from '@shared/composables/usePayload';
import { router } from '@inertiajs/vue3';
import type { VisitOptions } from '@inertiajs/core';
import type { ValuesOfConst } from '@shared/types/ApiResponse';

export function usePayoutRepository<T extends IPayout = IPayout>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Payout.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Payout.routes().update, {
                        id,
                    }),
                    method: Payout.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Payout.routes().destroy, {
                        id,
                    }),
                    method: Payout.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        const bulkUpdate = (ids: string[], status: ValuesOfConst<typeof PayoutStatus>) => {
            function execute(options?: VisitOptions) {
                router.patch(route('payouts.bulk-update'), { ids, status }, options);
            }

            return { execute };
        };

        return {
            store,
            update,
            destroy,
            bulkUpdate,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(Payout.routes().show, { id }) };
        };

        return {
            show,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
